import React, { useState, useEffect } from "react";
import SportModal from "./SportModal";
import { SearchInput, ConfirmDialog } from "../../admin";

const SportsTab = () => {
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSportModal, setShowSportModal] = useState(false);
  const [selectedSport, setSelectedSport] = useState(null);
  const [sportToDelete, setSportToDelete] = useState(null);
  const [search, setSearch] = useState("");

  // Fetch sports on component mount
  useEffect(() => {
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sports`);
      if (!response.ok) throw new Error("Failed to fetch sports");
      const data = await response.json();
      setSports(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSport = async (newSport) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newSport),
      });
      if (!response.ok) throw new Error("Failed to add sport");
      const data = await response.json();
      setSports([...sports, data]);
      setShowSportModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSport = async (sport) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sports/${sport.sportId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(sport),
        }
      );
      if (!response.ok) throw new Error("Failed to update sport");
      const updatedSport = await response.json();
      setSports(
        sports.map((s) => (s.sportId === sport.sportId ? updatedSport : s))
      );
      setShowSportModal(false);
      setSelectedSport(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSport = async () => {
    if (!sportToDelete) return;
    const sport = sportToDelete;

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sports/${sport.sportId}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) throw new Error("Failed to delete sport");
      setSports((currentSports) => currentSports.filter((s) => s.sportId !== sport.sportId));
      setSportToDelete(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* Derived: search filter */
  const filtered = sports.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.description?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search sports by name or description…"
        />
        <button
          type="button"
          className="btn-primary shrink-0"
          onClick={() => {
            setSelectedSport(null);
            setShowSportModal(true);
          }}
        >
          Add Sport
        </button>
      </div>

      {error && <div className="panel px-4 py-3 text-sm text-accent-soft border-accent-dim">{error}</div>}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-14 flex flex-col items-center text-gray-400">
            <div className="w-8 h-8 border-2 border-line-strong border-t-accent rounded-full animate-spin" />
            <p className="mt-4 text-sm">Loading sports…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-base font-semibold text-white">
              {search ? "No sports match your search" : "No sports yet"}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {search ? "Try a different term." : "Add your first sport to get started."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="px-3.5 py-2.5 text-left">Sport name</th>
                  <th className="px-3.5 py-2.5 text-left">Description</th>
                  <th className="px-3.5 py-2.5 text-right sticky right-0 bg-surface-600 z-10 border-l border-line-strong">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((sport) => (
                  <tr key={sport.sportId} className="table-row group">
                    <td className="px-3.5 py-2.5 text-white font-medium">{sport.name}</td>
                    <td className="px-3.5 py-2.5 text-gray-400 max-w-lg truncate">
                      {sport.description || "—"}
                    </td>
                    <td className="px-3.5 py-2 sticky right-0 bg-surface-850 group-hover:bg-surface-700 border-b border-l border-line transition-colors">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className="btn-ghost !py-1.5 !px-3"
                          onClick={() => {
                            setSelectedSport(sport);
                            setShowSportModal(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-danger !py-1.5 !px-3"
                          onClick={() => setSportToDelete(sport)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {sportToDelete && (
        <ConfirmDialog
          title="Delete sport"
          message={`Delete “${sportToDelete.name}”? This action cannot be undone.`}
          confirmLabel="Delete sport"
          onConfirm={handleDeleteSport}
          onCancel={() => setSportToDelete(null)}
          busy={loading}
        />
      )}

      {showSportModal && (
        <SportModal
          sport={selectedSport}
          onClose={() => {
            setShowSportModal(false);
            setSelectedSport(null);
          }}
          onSubmit={selectedSport ? handleEditSport : handleAddSport}
        />
      )}
    </div>
  );
};

export default SportsTab;
